import type {
    TermDTO,
    CoverageDTO
} from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/quote';
import {
    CHO_EXTENDED_COVERAGE,
    CHO_STANDARD_COVERAGE,
    PRH_LIABILITY_COV,
    PRH_PESTS_COV,
    PRH_PRIVATE_CONTENT_COV,
    PRH_WATER_DAMAGE_COV
} from '../../../constants/coverage';
import {
    PRH_PRIVATE_CONTENT_SUM_INSURED_TERM,
    PRH_PRIVATE_CONTENT_MANUAL_SUM,
    PRH_LIABILITY_DEDUCTIBLE_DISPLAY_NAME
} from '../../../constants/term';
import { formatStringToMonetaryValueString } from '../../../utils/coverablesUtil';
import { isInsuranceSumAboveThreshold } from '../../../utils/termsUtil';

const prhNonMonetaryStrings = [
    'PRHUnderExtensionCovExtensionEndDate',
    'PRHWaterProtectionServiceCovLeakbotType',
    'PRHWaterProtectionServiceCovLeakbotID',
    'PRHWaterProtectionServiceFree',
    'PRHWaterProtectionServiceWithPlumbing',
    'PRHWaterProtectionServiceNoPlumbing'
];

const coverageWithAlternateTitle = [
    PRH_WATER_DAMAGE_COV,
    PRH_PESTS_COV,
    PRH_PRIVATE_CONTENT_COV,
    CHO_STANDARD_COVERAGE,
    CHO_EXTENDED_COVERAGE
];

const varyingDeductibleModalTermConfig = (
    coverage: CoverageDTO,
    deductibleTerms: TermDTO[]
): TermDTO[] => {
    if (!isPrhCoverage(coverage.publicID)) return deductibleTerms;

    if (coverage.publicID !== PRH_PRIVATE_CONTENT_COV) {
        const displayedTerms = deductibleTerms.filter(
            (term) => term.publicId !== 'PRHWaterProtectionServiceCovLeakbotID' || !!term.value
        );
        if (coverage.publicID === PRH_LIABILITY_COV) {
            return [{ name: PRH_LIABILITY_DEDUCTIBLE_DISPLAY_NAME }, ...displayedTerms];
        }
        return displayedTerms;
    }
    const aboveThresholdContentSum = deductibleTerms.find(
        (term) =>
            term.publicId === PRH_PRIVATE_CONTENT_SUM_INSURED_TERM &&
            isInsuranceSumAboveThreshold(term.value)
    );
    const manualSumTerm = deductibleTerms.find(
        (term) => term.publicId === PRH_PRIVATE_CONTENT_MANUAL_SUM
    );

    const shouldShowManualSumAsSum = !!aboveThresholdContentSum && !!manualSumTerm;

    if (shouldShowManualSumAsSum)
        return deductibleTerms
            .map((term) => {
                if (term.publicId === PRH_PRIVATE_CONTENT_SUM_INSURED_TERM) {
                    return { ...term, value: manualSumTerm.value };
                }
                return term;
            })
            .filter((term) => term.publicId !== PRH_PRIVATE_CONTENT_MANUAL_SUM);

    return deductibleTerms.filter((term) => term.publicId !== PRH_PRIVATE_CONTENT_MANUAL_SUM);
};

const getFormattedTermValue = (term: TermDTO): string => {
    if (
        term.name === undefined ||
        term.value === undefined ||
        term.publicId === 'PRHPrivateContentCovNotice'
    ) {
        return '';
    }
    if (
        (term.publicId?.includes('DedPerc') || term.publicId?.includes('DeductiblePercentage')) &&
        !term.value.includes('%')
    ) {
        return `${term.value} %`;
    }
    if (
        term.publicId === 'CHOStandardCovIncreasedConstructionCosts' ||
        term.publicId === 'CHOExtendedCovIncreasedConstructionCosts'
    ) {
        return `${term.value}%`;
    }
    if (
        term.value.includes('%') ||
        term.name.includes('Selvrisikotype') ||
        term.value.includes('/') ||
        prhNonMonetaryStrings.includes(term.publicId ?? '') ||
        isInsuranceSumAboveThreshold(term.value) ||
        !isValueMonetaryOrNumber(term.value)
    ) {
        return `${term.value}`;
    }
    return `${formatStringToMonetaryValueString(term.value)} `;
};

const isPrhCoverage = (coveragePublicId: string = ''): boolean => {
    return coveragePublicId.toLowerCase().includes('prh');
};

const isValueMonetaryOrNumber = (value: string | undefined): boolean => {
    if (!value) return false;
    const isMonetary = value.includes('kr');
    const isNumber = !isNaN(Number(value));
    return isMonetary || isNumber;
};

const getTitleText = (coverage: CoverageDTO): string => {
    return coverageWithAlternateTitle.includes(coverage?.publicID ?? '')
        ? 'DSS-VARYING-DEDUCTIBLE-AND-SUM-LIMITATION-TITLE'
        : 'DSS-VARYING-DEDUCTIBLE-TITLE';
};

const getCoverageName = (coverage: CoverageDTO): string | undefined => {
    return isPrhCoverage(coverage.publicID) ? coverage.name?.toLocaleLowerCase() : coverage.name;
};

export { varyingDeductibleModalTermConfig, getFormattedTermValue, getTitleText, getCoverageName };
